import React, { useState, useEffect } from "react";
import { CustomTextBox } from "components/CustomComponents/CustomTextBox";
import { atleastOneCharacterWithSpace } from "services/constants/patterns";
import { CustomButton } from "components/CustomComponents";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { tooltipStyleProps } from "services/constants/Styles";
import { Cancel, AddCircleTwoTone } from "@material-ui/icons";
import { Delete } from "@mui/icons-material";
import {
  paymentOptions,
  shiftGradeOptions,
  CustomBox,
  cardStyle,
  boxStyle,
  infoMessageStyle,
  FullShiftCreditExample,
  PartialShiftCreditExample,
  WorkingHoursExample,
  BreakDeductionApplicabilityExample,
  mainOptions,
} from "../Component/ShiftCategoryOptions";
import {
  Box,
  Typography,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Select,
  MenuItem,
  Switch,
  TableContainer,
  TableCell,
  Tooltip,
  TableHead,
  IconButton,
  Grid,
  TextField,
  TableBody,
  Table,
  TableRow,
} from "@mui/material";
import { themes, tableHeaderSx, tableRowSx } from "services/constants";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import dayjs from "dayjs";

const getBreakDeductionTypeOptions = (ruleId) => [
  {
    id: "custom",
    label: "Custom Duration",
    description: "Use a specific deduction duration.",
  },
  {
    id: "shift_break",
    label: "Shift Break",
    description:
      ruleId === "insufficient_break"
        ? "The shortfall (difference) between the shift break duration and the recorded break time will be deducted."
        : "Use the break duration configured in the employee's shift.",
  },
];

const breakDeductionRuleOptions = [
  {
    id: "fixed_deduction",
    label: "Fixed Deduction",
    description:
      "Apply a fixed break deduction regardless of whether the employee takes the break or not.",
  },
  {
    id: "break_not_taken",
    label: "Break Not Taken",
    description: "Apply a break deduction when no break is recorded.",
  },
  {
    id: "insufficient_break",
    label: "Insufficient Break",
    description:
      "Apply an additional deduction when the recorded break is shorter than the required break duration.",
  },
];

const breakDeductionRuleFieldMap = {
  fixed_deduction: {
    flagField: "is_fixed_deduction",
    typeField: "fixed_deduction_type",
    durationField: "fixed_deduction_custom_duration",
    defaultDuration: 30,
  },
  break_not_taken: {
    flagField: "is_deduct_missed_breaks",
    typeField: "missed_break_deduction_type",
    durationField: "missed_break_custom_duration",
    defaultDuration: 30,
  },
  insufficient_break: {
    flagField: "is_deduct_short_breaks",
    typeField: "short_break_deduction_type",
    durationField: "short_break_custom_duration",
    defaultDuration: 20,
  },
};

const BreakDeductionRuleExample = ({ rule, amountType, duration }) => {
  const amountLabel =
    amountType === "custom" && duration
      ? `${duration} min`
      : "the shift's configured break duration";

  if (rule === "fixed_deduction") {
    return (
      <Typography variant="body2" color="text.secondary">
        <Box component="span" sx={{ fontWeight: 600, color: "primary.main" }}>
          e.g.,
        </Box>{" "}
        The actual recorded break time is always deducted. In addition,{" "}
        <Box component="span" fontWeight="bold">
          {amountLabel}
        </Box>{" "}
        will be deducted regardless of whether a break is taken.
      </Typography>
    );
  }

  if (rule === "break_not_taken") {
    return (
      <Typography variant="body2" color="text.secondary">
        <Box component="span" sx={{ fontWeight: 600, color: "primary.main" }}>
          e.g.,
        </Box>{" "}
        If the employee does not record any break,{" "}
        <Box component="span" fontWeight="bold">
          {amountLabel}
        </Box>{" "}
        will be deducted.
      </Typography>
    );
  }

  return (
    <Typography variant="body2" color="text.secondary">
      <Box component="span" sx={{ fontWeight: 600, color: "primary.main" }}>
        e.g.,
      </Box>{" "}
      If the recorded break is shorter than{" "}
      <Box component="span" fontWeight="bold">
        {amountLabel}
      </Box>
      , the shortfall will be deducted in addition to the recorded break
      time.
    </Typography>
  );
};

export const BasicInfo = ({
  onUpdate,
  handleNext,
  data,
  isEditScreen,
  showCategoryName,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...data,
      payment_based_on: "Full Shift Credit",
      is_partial_shift_work: false,
      is_include_sft_grade_sfts: "Shift Hours",
      additional_percentage: 0,
      shift_count: 0,
      rows: [{ work_min_percentage: "", count_as_shift: "" }],
      is_manual_break_allowed: false,
      manual_break_duration: 30,
      is_auto_deduct_break: false,
      is_fixed_deduction: false,
      fixed_deduction_type: "custom",
      fixed_deduction_custom_duration: 30,
      is_deduct_missed_breaks: false,
      missed_break_deduction_type: "custom",
      missed_break_custom_duration: 30,
      is_deduct_short_breaks: false,
      short_break_deduction_type: "custom",
      short_break_custom_duration: 20,
      break_deduction_applicability_percentage: 75,
      break_deduction_applicability_type: "Percentage",
      is_break_time_bound_break_deduction_allowed: false,
      break_time_bound_deduction_configurations: [],
    },
  });

  const { domain, token } = useSelector((state) => state.tokenReducer);
  const [rows, setRows] = useState([
    { work_min_percentage: "", count_as_shift: "" },
  ]);
  const [rowError, setRowError] = useState([
    { work_min_percentage: false, count_as_shift: false },
  ]);
  const [breakErrors, setBreakErrors] = useState({
    manual_break_duration: "",
    auto_deduct_rules: "",
    fixed_deduction_custom_duration: "",
    missed_break_custom_duration: "",
    short_break_custom_duration: "",
    break_deduction_applicability_percentage: "",
  });
  const [breakTimeBoundBreakRules, setBreakTimeBoundBreakRules] = useState([]);
  const [breakTimeBoundBreakErrors, setBreakTimeBoundBreakErrors] = useState(
    [],
  );

  const timeBoundBreakDeductFromOptions = [
    { value: "start_time", label: "Start" },
    { value: "mid_time", label: "Mid" },
    { value: "end_time", label: "End" },
  ];

  const createEmptyTimeBoundBreakRule = () => ({
    hours: 0,
    minutes: 0,
    deduct_minutes: 0,
    deduct_from: "start_time",
  });

  const getConfiguredTimeBoundBreakRows = (rulesRows) =>
    Array.isArray(rulesRows)
      ? rulesRows.filter(
          (row) =>
            row?.hours !== "" ||
            row?.minutes !== "" ||
            row?.deduct_minutes !== "",
        )
      : [];

  const validateTimeBoundBreakRuleRows = (rulesRows) =>
    rulesRows.map((row) => {
      if (
        row?.hours === "" &&
        row?.minutes === "" &&
        row?.deduct_minutes === ""
      ) {
        return { hours: false, minutes: false, deduct_minutes: false };
      }

      const hours = Number(row?.hours);
      const minutes = Number(row?.minutes);
      const deductMinutes = Number(row?.deduct_minutes);

      return {
        hours:
          row?.hours === "" || Number.isNaN(hours) || hours < 0 || hours > 24,
        minutes:
          row?.minutes === "" ||
          Number.isNaN(minutes) ||
          minutes < 0 ||
          minutes > 59,
        deduct_minutes:
          row?.deduct_minutes === "" ||
          Number.isNaN(deductMinutes) ||
          deductMinutes < 1 ||
          deductMinutes > 120,
      };
    });

  const getActiveBreakDeductionRule = () => {
    if (watch("is_fixed_deduction")) return "fixed_deduction";
    if (watch("is_deduct_missed_breaks")) return "break_not_taken";
    if (watch("is_deduct_short_breaks")) return "insufficient_break";
    return "";
  };

  const handleBreakDeductionRuleChange = (ruleId) => {
    Object.entries(breakDeductionRuleFieldMap).forEach(
      ([id, { flagField }]) => {
        setValue(flagField, id === ruleId);
      },
    );
    setBreakErrors((prev) => ({
      ...prev,
      auto_deduct_rules: "",
      fixed_deduction_custom_duration: "",
      missed_break_custom_duration: "",
      short_break_custom_duration: "",
    }));
  };

  const handleBreakDeductionApplicabilityTypeChange = (event) => {
    const value = event.target.value;
    setValue("break_deduction_applicability_type", value);
    setBreakErrors((prev) => ({
      ...prev,
      break_deduction_applicability_percentage: "",
    }));
    if (value !== "Hours") {
      setValue("is_break_time_bound_break_deduction_allowed", false);
      resetBreakTimeBoundBreakRules();
    } else {
      // Hours mode covers missed/short/fixed breaks via the time-bound
      // rules, so the break deduction rule selector no longer applies.
      setValue("is_fixed_deduction", false);
      setValue("is_deduct_missed_breaks", false);
      setValue("is_deduct_short_breaks", false);
      setValue("fixed_deduction_type", "custom");
      setValue("missed_break_deduction_type", "custom");
      setValue("short_break_deduction_type", "custom");
      setValue("fixed_deduction_custom_duration", 30);
      setValue("missed_break_custom_duration", 30);
      setValue("short_break_custom_duration", 20);
      setBreakErrors((prev) => ({
        ...prev,
        auto_deduct_rules: "",
        fixed_deduction_custom_duration: "",
        missed_break_custom_duration: "",
        short_break_custom_duration: "",
      }));
    }
  };

  const resetBreakTimeBoundBreakRules = () => {
    setBreakTimeBoundBreakRules([]);
    setBreakTimeBoundBreakErrors([]);
    setValue("break_time_bound_deduction_configurations", [], {
      shouldValidate: false,
      shouldDirty: true,
    });
  };

  // Manual Break and Auto-Deduct Break Time are mutually exclusive.
  const disableAutoDeductBreak = () => {
    setValue("is_auto_deduct_break", false);
    setValue("is_fixed_deduction", false);
    setValue("is_deduct_missed_breaks", false);
    setValue("is_deduct_short_breaks", false);
    setValue("fixed_deduction_type", "custom");
    setValue("missed_break_deduction_type", "custom");
    setValue("short_break_deduction_type", "custom");
    setValue("fixed_deduction_custom_duration", 30);
    setValue("missed_break_custom_duration", 30);
    setValue("short_break_custom_duration", 20);
    setValue("break_deduction_applicability_percentage", 75);
    setValue("is_break_time_bound_break_deduction_allowed", false);
    resetBreakTimeBoundBreakRules();
    setBreakErrors((prev) => ({
      ...prev,
      auto_deduct_rules: "",
      fixed_deduction_custom_duration: "",
      missed_break_custom_duration: "",
      short_break_custom_duration: "",
      break_deduction_applicability_percentage: "",
    }));
  };

  const disableManualBreak = () => {
    setValue("is_manual_break_allowed", false);
    setValue("manual_break_duration", 30);
    setBreakErrors((prev) => ({
      ...prev,
      manual_break_duration: "",
    }));
  };

  const handleBreakTimeBoundBreakToggle = (event) => {
    const checked = event.target.checked;
    setValue("is_break_time_bound_break_deduction_allowed", checked);
    if (checked) {
      if (breakTimeBoundBreakRules.length === 0) {
        setBreakTimeBoundBreakRules([createEmptyTimeBoundBreakRule()]);
        setBreakTimeBoundBreakErrors([
          { hours: false, minutes: false, deduct_minutes: false },
        ]);
      }
    } else {
      resetBreakTimeBoundBreakRules();
    }
  };

  const handleAddBreakTimeBoundBreakRule = () => {
    setBreakTimeBoundBreakRules((prev) => [
      ...prev,
      createEmptyTimeBoundBreakRule(),
    ]);
    setBreakTimeBoundBreakErrors((prev) => [
      ...prev,
      { hours: false, minutes: false, deduct_minutes: false },
    ]);
  };

  const handleRemoveBreakTimeBoundBreakRule = (index) => {
    if (breakTimeBoundBreakRules.length <= 1) {
      return;
    }
    setBreakTimeBoundBreakRules((prev) => prev.filter((_, i) => i !== index));
    setBreakTimeBoundBreakErrors((prev) => prev.filter((_, i) => i !== index));
  };

  const handleBreakTimeBoundBreakRuleChange = (index, field, value) => {
    setBreakTimeBoundBreakRules((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );
    setBreakTimeBoundBreakErrors((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: false } : row)),
    );
  };

  // Backend stores the threshold as separate hours/minutes, so the picker's
  // dayjs value is built from and split back into those two fields.
  const getTimeBoundBreakPickerValue = (rule) => {
    if (rule?.hours === "" || rule?.minutes === "") {
      return null;
    }
    const hours = Number(rule?.hours);
    const minutes = Number(rule?.minutes);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return null;
    }
    return dayjs().hour(Math.min(hours, 23)).minute(minutes).second(0);
  };

  const handleTimeBoundBreakTimeChange = (index, newValue) => {
    if (!newValue || !newValue.isValid()) {
      handleBreakTimeBoundBreakRuleChange(index, "hours", "");
      handleBreakTimeBoundBreakRuleChange(index, "minutes", "");
      return;
    }
    handleBreakTimeBoundBreakRuleChange(index, "hours", newValue.hour());
    handleBreakTimeBoundBreakRuleChange(index, "minutes", newValue.minute());
  };

  const validateBreakTimeBoundBreakRules = () => {
    if (!watch("is_break_time_bound_break_deduction_allowed")) {
      setBreakTimeBoundBreakErrors([]);
      return true;
    }

    const nextErrors = validateTimeBoundBreakRuleRows(breakTimeBoundBreakRules);
    setBreakTimeBoundBreakErrors(nextErrors);
    return !nextErrors.some(
      (row) => row?.hours || row?.minutes || row?.deduct_minutes,
    );
  };

  const updateShiftCategoryName = (event) => {
    setValue("sft_category_name", event.target.value);
  };

  const hanldePaymentBasedOn = (event) => {
    setValue("payment_based_on", event.target.value);
    setValue("is_partial_shift_work", false);
    setRows([{ work_min_percentage: "", count_as_shift: "" }]);
    setRowError([{ work_min_percentage: false, count_as_shift: false }]);
    setValue("additional_percentage", 0);
    setValue("shift_count", 0);
  };

  const handleSftGradeChange = (event) => {
    setValue("is_include_sft_grade_sfts", event.target.value);
  };

  const handleBreakNumberChange = (field, value, max = null) => {
    if (value === "") {
      setValue(field, "");
      return;
    }

    if (!/^\d{0,3}$/.test(value)) {
      return;
    }

    const numericValue = Number(value);
    if (max !== null && numericValue > max) {
      return;
    }

    setValue(field, value);
    setBreakErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  const validateBreakPolicy = () => {
    const nextErrors = {
      manual_break_duration: "",
      auto_deduct_rules: "",
      fixed_deduction_custom_duration: "",
      missed_break_custom_duration: "",
      short_break_custom_duration: "",
      break_deduction_applicability_percentage: "",
    };

    if (watch("is_manual_break_allowed")) {
      const manualDuration = Number(watch("manual_break_duration"));
      if (!watch("manual_break_duration")) {
        nextErrors.manual_break_duration = "Enter value";
      } else if (manualDuration <= 0) {
        nextErrors.manual_break_duration = "Enter value";
      }
    }

    if (watch("is_auto_deduct_break")) {
      const isHoursApplicability =
        watch("break_deduction_applicability_type") === "Hours";

      // In Hours mode, break deductions are handled by the time-bound
      // rules instead, so the rule selector isn't shown or required.
      if (!isHoursApplicability) {
        const activeRule = getActiveBreakDeductionRule();
        if (!activeRule) {
          nextErrors.auto_deduct_rules = "Select a break deduction rule";
        } else {
          const { typeField, durationField } =
            breakDeductionRuleFieldMap[activeRule];
          if (watch(typeField) === "custom") {
            const duration = Number(watch(durationField));
            if (!watch(durationField)) {
              nextErrors[durationField] = "Enter value";
            } else if (duration <= 0) {
              nextErrors[durationField] = "Enter valid value";
            }
          }
        }
      }

      if (!isHoursApplicability) {
        const applicabilityPercentage = Number(
          watch("break_deduction_applicability_percentage"),
        );
        if (!watch("break_deduction_applicability_percentage")) {
          nextErrors.break_deduction_applicability_percentage = "Enter value";
        } else if (
          applicabilityPercentage <= 0 ||
          applicabilityPercentage > 999
        ) {
          nextErrors.break_deduction_applicability_percentage =
            "Enter valid value";
        }
      }
    }

    setBreakErrors(nextErrors);
    return !Object.values(nextErrors).some(Boolean);
  };

  const validateRows = () => {
    const newErrors = rows.map((row, index) => {
      const minEmpty = row.work_min_percentage === "";
      const compEmpty = row.count_as_shift === "";

      let compInvalid = false;
      let minInvalid = false;

      if (!compEmpty && index > 0) {
        const current = parseFloat(row.count_as_shift);
        const previous = parseFloat(rows[index - 1].count_as_shift);

        if (!isNaN(current) && !isNaN(previous)) {
          const isPrevInteger = Number.isInteger(previous);
          if (isPrevInteger) {
            compInvalid = current < previous;
          } else {
            compInvalid = current <= previous;
          }
        } else {
          compInvalid = true;
        }
      }

      if (!minEmpty && index > 0) {
        const currentMin = parseFloat(row.work_min_percentage);
        const prevMin = parseFloat(rows[index - 1].work_min_percentage);
        minInvalid =
          isNaN(currentMin) || isNaN(prevMin) || currentMin <= prevMin;
      }

      return {
        work_min_percentage: minEmpty || minInvalid,
        count_as_shift: compEmpty || compInvalid,
      };
    });

    const hasError = newErrors.some(
      (err) => err.work_min_percentage || err.count_as_shift,
    );

    setRowError(newErrors);
    return !hasError;
  };

  const handleAddRow = () => {
    const isValid = validateRows();
    if (!isValid) return;

    setRows((prev) => [
      ...prev,
      { work_min_percentage: "", count_as_shift: "" },
    ]);
    setRowError((prev) => [
      ...prev,
      { work_min_percentage: false, count_as_shift: false },
    ]);
  };

  const handleRemoveRow = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
    setRowError((prev) => prev.filter((_, i) => i !== index));
  };

  const handleChange = (index, field, value) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = value;
    setRows(updatedRows);

    const updatedErrors = [...rowError];
    if (!updatedErrors[index])
      updatedErrors[index] = {
        work_min_percentage: false,
        count_as_shift: false,
      };
    updatedErrors[index][field] = false;
    setRowError(updatedErrors);
  };

  const onSubmit = async (formValues) => {
    const isPaymentValid =
      watch("payment_based_on") === "Partial Shift Credit"
        ? validateRows()
        : true;
    const isBreakPolicyValid = validateBreakPolicy();
    const isBreakTimeBoundBreakValid =
      watch("is_auto_deduct_break") &&
      watch("break_deduction_applicability_type") === "Hours"
        ? validateBreakTimeBoundBreakRules()
        : true;
    if (isPaymentValid && isBreakPolicyValid && isBreakTimeBoundBreakValid) {
      onUpdate({
        ...formValues,
        rows,
        break_time_bound_deduction_configurations: watch(
          "is_break_time_bound_break_deduction_allowed",
        )
          ? getConfiguredTimeBoundBreakRows(breakTimeBoundBreakRules)
          : [],
      });
      handleNext(1);
    }
  };

  useEffect(() => {
    if (data && Object.keys(data)?.length > 0) {
      reset(data);
      const mappedRows = data?.rows?.map((item) => ({
        work_min_percentage: item.work_min_percentage,
        count_as_shift: item.count_as_shift,
      }));
      setRows(mappedRows);
      setBreakErrors({
        manual_break_duration: "",
        auto_deduct_rules: "",
        fixed_deduction_custom_duration: "",
        missed_break_custom_duration: "",
        short_break_custom_duration: "",
        break_deduction_applicability_percentage: "",
      });
      const mappedBreakTimeBoundBreakRules = Array.isArray(
        data?.break_time_bound_deduction_configurations,
      )
        ? data.break_time_bound_deduction_configurations.map((item) => ({
            hours: item?.hours ?? 0,
            minutes: item?.minutes ?? 0,
            deduct_minutes: item?.deduct_minutes ?? 0,
            deduct_from: item?.deduct_from || "start_time",
          }))
        : [];
      setValue(
        "is_break_time_bound_break_deduction_allowed",
        mappedBreakTimeBoundBreakRules.length > 0,
      );
      setBreakTimeBoundBreakRules(mappedBreakTimeBoundBreakRules);
      setBreakTimeBoundBreakErrors(
        mappedBreakTimeBoundBreakRules.map(() => ({
          hours: false,
          minutes: false,
          deduct_minutes: false,
        })),
      );
    }
  }, [data, reset, setValue]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{ flexGrow: 1, overflow: "auto", p: 2 }}
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
          }}
        >
          <Box sx={{ ml: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Shift Category Name{" "}
              <span style={{ color: themes?.redColor }}>*</span>
            </Typography>
            <CustomTextBox
              selectedValue={watch("sft_category_name")}
              rules={{
                ...register("sft_category_name", {
                  required: "Shift category name is required",
                  minLength: {
                    value: 3,
                    message: "Minium length is 3 characters",
                  },
                  maxLength: {
                    value: 100,
                    message: "Maximum length is 100 characters",
                  },
                  validate: {
                    atleastOneCharacterWithSpaceReg: (value) => {
                      if (
                        atleastOneCharacterWithSpace.test(value) ||
                        value === ""
                      ) {
                        return true;
                      } else {
                        return "Atleast one alphabet required";
                      }
                    },

                    checkShiftUniqueName: async (fieldValue) => {
                      const editUniqueCheck =
                        fieldValue !== "" &&
                        watch("sft_category_name") !== data?.sftCategoryName;
                      if (isEditScreen ? editUniqueCheck : fieldValue !== "") {
                        const response = await fetch(
                          `${domain}unique/shift-category/?name=${fieldValue.trim()}`,
                          {
                            method: "GET",
                            headers: {
                              "Content-Type": "application/json",
                              Authorization: `token ${token}`,
                            },
                          },
                        );
                        const resp = await response.json();
                        if (resp.exists) {
                          return "Shift category name already exists";
                        }
                      }
                    },
                  },
                }),
              }}
              updateDataFunction={updateShiftCategoryName}
              placeHolder="Enter shift category name"
              errorMessage={
                errors?.sft_category_name?.message
                  ? errors?.sft_category_name?.message
                  : ""
              }
              textBoxError={errors.sft_category_name ? true : false}
            />
          </Box>
          {showCategoryName && (
            <Box sx={{ display: "flex", alignItems: "center", ml: 1, gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Cloned From :
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ color: themes.stepActiveColor, fontWeight: 600 }}
              >
                {data?.sftCategoryName}
              </Typography>
            </Box>
          )}

          <Divider sx={{ borderColor: themes?.miliedLightGray }} />

          <Box sx={{ ml: 1 }}>
            <Typography fontWeight="medium">Payment Based On</Typography>
            <Typography variant="body2" color="text.secondary">
              Define the payment calculation method with this option.
            </Typography>
          </Box>

          <Box>
            <RadioGroup
              row
              value={watch("payment_based_on")}
              onChange={hanldePaymentBasedOn}
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                ml: 0.8,
              }}
            >
              {paymentOptions.map((opt) => {
                const isActive = watch("payment_based_on") === opt.id;
                return (
                  <Box key={opt.id} sx={cardStyle(isActive)}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Radio value={opt.id} />
                      <Typography
                        fontWeight={"medium"}
                        sx={{
                          color:
                            watch("payment_based_on") === opt.id
                              ? themes?.stepActiveColor
                              : "inherit",
                        }}
                      >
                        {opt.label}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        marginLeft: "32px",
                        maxWidth: 250,
                        wordBreak: "break-word",
                      }}
                    >
                      {opt.description}
                    </Typography>
                  </Box>
                );
              })}
            </RadioGroup>
          </Box>

          {watch("payment_based_on") === "Full Shift Credit" && (
            <Box sx={{ ...infoMessageStyle, ml: 0, mr: 0 }}>
              <FullShiftCreditExample />
            </Box>
          )}

          {watch("payment_based_on") === "Hours" && (
            <Box sx={{ ...infoMessageStyle, ml: 0, mr: 0 }}>
              <WorkingHoursExample />
            </Box>
          )}

          {watch("payment_based_on") === "Partial Shift Credit" && (
            <Box
              sx={{
                border: `1px solid ${themes.miliedLightGray}`,
                p: 2,
                borderRadius: 2,
                ml: 0.8,
              }}
            >
              <Box>
                <TableContainer
                  sx={{
                    width: "100%",
                  }}
                >
                  <Table className="border">
                    <TableHead sx={tableHeaderSx}>
                      <TableRow maxWidth="xl" align="left" sx={tableRowSx}>
                        <TableCell
                          sx={{
                            color: themes.headLine,
                            fontWeight: "bold",
                          }}
                        >
                          Min % Completed
                        </TableCell>{" "}
                        <TableCell
                          sx={{
                            color: themes.headLine,
                            fontWeight: "bold",
                          }}
                        >
                          Count as Shift
                        </TableCell>{" "}
                        <TableCell
                          sx={{
                            color: themes.headLine,
                            fontWeight: "bold",
                          }}
                        >
                          Action
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    {rows.map((row, index) => (
                      <TableBody>
                        <TableCell>
                          <TextField
                            size="small"
                            value={row.work_min_percentage}
                            onChange={(e) => {
                              const val = e.target.value;
                              const validPattern = /^\d{1,3}(\.\d{0,2})?$/;

                              if (val === "" || validPattern.test(val)) {
                                handleChange(index, "work_min_percentage", val);
                              }
                            }}
                            error={
                              rowError?.[index]?.work_min_percentage || false
                            }
                            helperText={
                              rowError[index]?.work_min_percentage
                                ? row.work_min_percentage.trim() === ""
                                  ? "Enter percentage"
                                  : index > 0 &&
                                      parseFloat(row.work_min_percentage) <=
                                        parseFloat(
                                          rows[index - 1].work_min_percentage,
                                        )
                                    ? `Percentage must be greater than  ${
                                        rows[index - 1]?.work_min_percentage
                                      }`
                                    : ""
                                : ""
                            }
                            placeholder="Enter percentage"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={row.count_as_shift}
                            onChange={(e) => {
                              const val = e.target.value;
                              const validPattern = /^[0-9]?(\.\d{0,2})?$/;
                              if (val === "" || validPattern.test(val)) {
                                handleChange(index, "count_as_shift", val);
                              }
                            }}
                            error={rowError[index]?.count_as_shift}
                            helperText={
                              rowError[index]?.count_as_shift
                                ? row.count_as_shift.trim() === ""
                                  ? "Enter Value"
                                  : index > 0 &&
                                      parseFloat(row.count_as_shift) <=
                                        parseFloat(
                                          rows[index - 1].count_as_shift,
                                        )
                                    ? `Value must be greater than  ${
                                        rows[index - 1]?.count_as_shift
                                      }`
                                    : ""
                                : ""
                            }
                            placeholder="Enter value"
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip
                            arrow
                            slotProps={tooltipStyleProps}
                            title={"Remove"}
                          >
                            <IconButton
                              onClick={() => handleRemoveRow(index)}
                              disabled={rows?.length === 1}
                            >
                              <Cancel />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableBody>
                    ))}
                  </Table>
                </TableContainer>
                <Grid container>
                  <Grid item xs={10.75}></Grid>
                  <Grid item xs={1}>
                    <Tooltip
                      arrow
                      slotProps={tooltipStyleProps}
                      title={"Add Row"}
                    >
                      <IconButton onClick={handleAddRow}>
                        <AddCircleTwoTone />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
                <Box
                  sx={{
                    border: `1px solid ${themes.miliedLightGray}`,
                    p: 1,
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Each additional
                    </Typography>

                    <CustomBox
                      name="additional_percentage"
                      type="percentage"
                      value={watch("additional_percentage")}
                      register={register}
                      errors={errors}
                      handleChange={(e, value) => {
                        setValue("additional_percentage", value);
                      }}
                      rules={{ required: "Enter value" }}
                      inputWidth="50px"
                    />

                    <Typography variant="body2" color="text.secondary">
                      percentage is counted as
                    </Typography>
                    <CustomBox
                      name="shift_count"
                      type="shift"
                      value={watch("shift_count")}
                      register={register}
                      errors={errors}
                      handleChange={(e, value) => {
                        setValue("shift_count", value);
                      }}
                      rules={{ required: "Enter value" }}
                      inputWidth="50px"
                    />
                    <Typography variant="body2" color="text.secondary">
                      shift
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ ...infoMessageStyle, ml: 0, mr: 0 }}>
                  <PartialShiftCreditExample />
                </Box>
              </Box>
            </Box>
          )}

          <Box
            sx={{
              border: `1px solid ${themes.miliedLightGray}`,
              p: 2,
              borderRadius: 2,
              ml: 0.8,
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Box>
                <Typography fontWeight="medium">
                  Payment hours calculation based on
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sets how working hours are managed when employees work shifts
                  from different grade levels.
                </Typography>
              </Box>
            </Box>
            <RadioGroup
              value={watch("is_include_sft_grade_sfts")}
              onChange={handleSftGradeChange}
            >
              {shiftGradeOptions.map((opt) => {
                const isActive = watch("is_include_sft_grade_sfts") === opt.id;
                return (
                  <Box key={opt.id} sx={{ ...cardStyle(isActive), mt: 1 }}>
                    <FormControlLabel
                      value={opt.id}
                      control={<Radio />}
                      label={
                        <Box display="flex" flexDirection="column">
                          <Typography
                            fontWeight={"medium"}
                            sx={{
                              color:
                                watch("is_include_sft_grade_sfts") === opt.id
                                  ? themes.stepActiveColor
                                  : "inherit",
                            }}
                          >
                            {opt.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {opt.description}
                          </Typography>
                        </Box>
                      }
                      sx={{ width: "100%", m: 0 }}
                    />
                  </Box>
                );
              })}
            </RadioGroup>
          </Box>

          <Box sx={{ ...mainOptions, ml: 0.8 }}>
            <Box sx={boxStyle(watch("is_manual_break_allowed"))}>
              <Box>
                <Typography fontWeight="medium">Allow Manual Break</Typography>
                <Typography variant="body2" color="text.secondary">
                  Allow employees to apply break manually.
                </Typography>
              </Box>
              <Switch
                checked={watch("is_manual_break_allowed")}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setValue("is_manual_break_allowed", checked);
                  setBreakErrors((prev) => ({
                    ...prev,
                    manual_break_duration: "",
                  }));
                  if (!checked) {
                    setValue("manual_break_duration", 30);
                  } else if (watch("is_auto_deduct_break")) {
                    disableAutoDeductBreak();
                  }
                }}
              />
            </Box>

            {watch("is_manual_break_allowed") && (
              <Box sx={{ px: 2, pb: 2 }}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${themes.miliedLightGray}`,
                    backgroundColor: "#F7FBF9",
                  }}
                >
                  <Typography fontWeight="medium" sx={{ mb: 0.5 }}>
                    Default Break Duration
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1.5 }}
                  >
                    Standard duration. Employees can adjust this when logging
                    their break.
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1.25}>
                    <TextField
                      size="small"
                      value={watch("manual_break_duration")}
                      onChange={(event) =>
                        handleBreakNumberChange(
                          "manual_break_duration",
                          event.target.value,
                        )
                      }
                      sx={{ width: 96 }}
                      error={Boolean(breakErrors.manual_break_duration)}
                      helperText={breakErrors.manual_break_duration}
                    />
                    <Typography variant="body2" color="text.secondary">
                      mins
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>

          <Box sx={{ ...mainOptions, ml: 0.8 }}>
            <Box sx={boxStyle(watch("is_auto_deduct_break"))}>
              <Box>
                <Typography fontWeight="medium">
                  Auto-Deduct Break Time
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Auto-deduct break time from total worked hours if breaks are
                  missed or fall short.
                </Typography>
              </Box>
              <Switch
                checked={watch("is_auto_deduct_break")}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setValue("is_auto_deduct_break", checked);
                  setBreakErrors((prev) => ({
                    ...prev,
                    auto_deduct_rules: "",
                    fixed_deduction_custom_duration: "",
                    missed_break_custom_duration: "",
                    short_break_custom_duration: "",
                    break_deduction_applicability_percentage: "",
                  }));
                  if (!checked) {
                    setValue("is_fixed_deduction", false);
                    setValue("is_deduct_missed_breaks", false);
                    setValue("is_deduct_short_breaks", false);
                    setValue("fixed_deduction_type", "custom");
                    setValue("missed_break_deduction_type", "custom");
                    setValue("short_break_deduction_type", "custom");
                    setValue("fixed_deduction_custom_duration", 30);
                    setValue("missed_break_custom_duration", 30);
                    setValue("short_break_custom_duration", 20);
                    setValue("break_deduction_applicability_percentage", 75);
                    setValue(
                      "is_break_time_bound_break_deduction_allowed",
                      false,
                    );
                    resetBreakTimeBoundBreakRules();
                  } else {
                    if (watch("is_manual_break_allowed")) {
                      disableManualBreak();
                    }
                    if (!getActiveBreakDeductionRule()) {
                      setValue("is_fixed_deduction", true);
                    }
                  }
                }}
              />
            </Box>

            {watch("is_auto_deduct_break") && (
              <Box
                sx={{
                  px: 2,
                  pb: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                {breakErrors.auto_deduct_rules && (
                  <Typography
                    variant="body2"
                    sx={{ color: themes.redColor, mt: -0.5 }}
                  >
                    {breakErrors.auto_deduct_rules}
                  </Typography>
                )}
                <Box
                  sx={{
                    border: `1px solid ${themes.miliedLightGray}`,
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: `1px solid ${themes.miliedLightGray}`,
                      backgroundColor: "#F7FBF9",
                    }}
                  >
                    <Typography fontWeight="medium" sx={{ mb: 0.5 }}>
                      Deduction Applicability
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1.5 }}
                    >
                      Choose whether break deductions apply based on a minimum
                      worked percentage of the scheduled shift, or based on
                      total hours worked.
                    </Typography>
                    <RadioGroup
                      row
                      value={
                        watch("break_deduction_applicability_type") ||
                        "Percentage"
                      }
                      onChange={handleBreakDeductionApplicabilityTypeChange}
                      sx={{ mb: 1.5 }}
                    >
                      <FormControlLabel
                        value="Percentage"
                        control={<Radio />}
                        label="Percentage"
                      />
                      <FormControlLabel
                        value="Hours"
                        control={<Radio />}
                        label="Hours"
                      />
                    </RadioGroup>
                    {watch("break_deduction_applicability_type") !== "Hours" ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "stretch",
                          gap: 1.5,
                          flexWrap: "wrap",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            minHeight: 64,
                          }}
                        >
                          <TextField
                            size="small"
                            value={watch(
                              "break_deduction_applicability_percentage",
                            )}
                            onChange={(event) =>
                              handleBreakNumberChange(
                                "break_deduction_applicability_percentage",
                                event.target.value,
                                999,
                              )
                            }
                            sx={{ width: 96 }}
                            error={Boolean(
                              breakErrors.break_deduction_applicability_percentage,
                            )}
                            helperText={
                              breakErrors.break_deduction_applicability_percentage
                            }
                          />
                          <Typography variant="body2" color="text.secondary">
                            %
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            ...infoMessageStyle,
                            ml: 0,
                            mr: 0,
                            flex: "1 1 280px",
                            maxWidth: 420,
                            display: "flex",
                            alignItems: "center",
                            minHeight: 64,
                            mb: 0,
                          }}
                        >
                          <BreakDeductionApplicabilityExample />
                        </Box>
                      </Box>
                    ) : (
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Box>
                            <Typography fontWeight="medium" sx={{ mb: 0.5 }}>
                              Time-Bound Break Deductions
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Enable this to deduct break time automatically
                              based on total hours worked.
                            </Typography>
                            <Box
                              sx={{
                                display: "inline-block",
                                mb: 1.5,
                                mt: 1,
                                px: 1.25,
                                py: 0.75,
                                backgroundColor: "#f0fdf4",
                                borderLeft: "3px solid #22c55e",
                                color: "#166534",
                                borderRadius: "0 4px 4px 0",
                                fontSize: 12,
                              }}
                            >
                              <strong>Note:</strong> If an employee records a
                              break, it counts toward the rule and only the
                              remaining break time is deducted.
                            </Box>
                          </Box>
                          <Switch
                            checked={watch(
                              "is_break_time_bound_break_deduction_allowed",
                            )}
                            onChange={handleBreakTimeBoundBreakToggle}
                          />
                        </Box>
                        {watch(
                          "is_break_time_bound_break_deduction_allowed",
                        ) && (
                          <>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                mb: 1.5,
                              }}
                            >
                              <Box
                                component="button"
                                type="button"
                                onClick={handleAddBreakTimeBoundBreakRule}
                                sx={{
                                  minHeight: 38,
                                  px: 2,
                                  borderRadius: "10px",
                                  border: "1px dashed #7BC9A7",
                                  backgroundColor: "#F8FCFA",
                                  color: "#1A5C42",
                                  fontSize: "inherit",
                                  fontWeight: "inherit",
                                  cursor: "pointer",
                                  textAlign: "left",
                                  whiteSpace: "nowrap",
                                  width: "fit-content",
                                  maxWidth: "100%",
                                  "&:hover": {
                                    backgroundColor: "#F1FAF5",
                                    borderColor: "#4FB489",
                                  },
                                }}
                              >
                                <Typography
                                  fontWeight={600}
                                  sx={{
                                    color: "#1A5C42",
                                    fontSize: 14,
                                    lineHeight: 1.25,
                                  }}
                                >
                                  + Add Time Boundary
                                </Typography>
                              </Box>
                            </Box>
                            {breakTimeBoundBreakRules.map((rule, index) => (
                              <Box
                                key={`break-time-bound-break-${index}`}
                                sx={{
                                  backgroundColor: "#f4f6f9",
                                  border: "1px solid #d1d5db",
                                  borderRadius: "6px",
                                  p: 2,
                                  mb: 1.5,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    gap: 1,
                                    mb: 1.5,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      flexWrap: "wrap",
                                      gap: 1,
                                    }}
                                  >
                                    <Typography variant="body2">
                                      <strong>Rule {index + 1}:</strong> If
                                      total worked hours exceed
                                    </Typography>
                                    <LocalizationProvider
                                      dateAdapter={AdapterDayjs}
                                    >
                                      <TimePicker
                                        ampm={false}
                                        viewRenderers={{
                                          hours: renderTimeViewClock,
                                          minutes: renderTimeViewClock,
                                        }}
                                        value={getTimeBoundBreakPickerValue(
                                          rule,
                                        )}
                                        onChange={(newValue) =>
                                          handleTimeBoundBreakTimeChange(
                                            index,
                                            newValue,
                                          )
                                        }
                                        slotProps={{
                                          textField: {
                                            size: "small",
                                            error: Boolean(
                                              breakTimeBoundBreakErrors?.[index]
                                                ?.hours ||
                                              breakTimeBoundBreakErrors?.[index]
                                                ?.minutes,
                                            ),
                                            sx: { width: 130 },
                                          },
                                        }}
                                      />
                                    </LocalizationProvider>
                                  </Box>
                                  <Box
                                    onClick={() =>
                                      breakTimeBoundBreakRules.length > 1 &&
                                      handleRemoveBreakTimeBoundBreakRule(index)
                                    }
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                      cursor:
                                        breakTimeBoundBreakRules.length > 1
                                          ? "pointer"
                                          : "not-allowed",
                                      color:
                                        breakTimeBoundBreakRules.length > 1
                                          ? "#dc2626"
                                          : "#9ca3af",
                                      fontWeight: 600,
                                      fontSize: 13,
                                      "&:hover": {
                                        textDecoration:
                                          breakTimeBoundBreakRules.length > 1
                                            ? "underline"
                                            : "none",
                                      },
                                    }}
                                  >
                                    <Delete fontSize="small" />
                                    Remove
                                  </Box>
                                </Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    ml: 5.5,
                                    color: "#4b5563",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "inherit" }}
                                  >
                                    Apply:
                                  </Typography>
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={rule.deduct_minutes}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      if (
                                        /^\d{0,3}$/.test(val) &&
                                        (val === "" || Number(val) <= 120)
                                      ) {
                                        handleBreakTimeBoundBreakRuleChange(
                                          index,
                                          "deduct_minutes",
                                          val,
                                        );
                                      }
                                    }}
                                    error={Boolean(
                                      breakTimeBoundBreakErrors?.[index]
                                        ?.deduct_minutes,
                                    )}
                                    inputProps={{
                                      min: 1,
                                      max: 120,
                                      style: {
                                        textAlign: "center",
                                        width: "50px",
                                        padding: "4px 6px",
                                      },
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "inherit" }}
                                  >
                                    mins break at
                                  </Typography>
                                  <FormControl
                                    size="small"
                                    sx={{ minWidth: 115 }}
                                  >
                                    <Select
                                      value={rule.deduct_from || "start_time"}
                                      onChange={(event) =>
                                        handleBreakTimeBoundBreakRuleChange(
                                          index,
                                          "deduct_from",
                                          event.target.value,
                                        )
                                      }
                                      sx={{
                                        backgroundColor: "#fff",
                                        "& .MuiSelect-select": {
                                          py: "6px",
                                        },
                                      }}
                                    >
                                      {timeBoundBreakDeductFromOptions.map(
                                        (option) => (
                                          <MenuItem
                                            key={option.value}
                                            value={option.value}
                                          >
                                            {option.label}
                                          </MenuItem>
                                        ),
                                      )}
                                    </Select>
                                  </FormControl>
                                </Box>
                              </Box>
                            ))}
                            {breakTimeBoundBreakErrors.some(
                              (row) =>
                                row?.hours ||
                                row?.minutes ||
                                row?.deduct_minutes,
                            ) && (
                              <Typography
                                variant="body2"
                                color="error"
                                sx={{ mb: 1 }}
                              >
                                Enter a valid threshold (0-24 hrs, 0-59 mins)
                                and a deduction between 1-120 mins for each
                                rule.
                              </Typography>
                            )}
                          </>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>

                {watch("break_deduction_applicability_type") !== "Hours" && (
                  <Box
                    sx={{
                      border: `1px solid ${themes.miliedLightGray}`,
                      borderRadius: 2,
                      overflow: "hidden",
                      p: 2,
                    }}
                  >
                    <Typography fontWeight="medium" sx={{ mb: 0.5 }}>
                      Break Deduction Rule
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1.5 }}
                    >
                      Select the break deduction rule to apply when an
                      employee does not take the full scheduled break.
                    </Typography>
                    <RadioGroup
                      row
                      value={getActiveBreakDeductionRule()}
                      onChange={(event) =>
                        handleBreakDeductionRuleChange(event.target.value)
                      }
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(3, 1fr)",
                        },
                        gap: 2,
                      }}
                    >
                      {breakDeductionRuleOptions.map((opt) => {
                        const isActive =
                          getActiveBreakDeductionRule() === opt.id;
                        return (
                          <Box
                            key={opt.id}
                            sx={{
                              ...cardStyle(isActive),
                              m: 0,
                            }}
                          >
                            <Box
                              sx={{ display: "flex", alignItems: "flex-start" }}
                            >
                              <Radio
                                value={opt.id}
                                sx={{ p: 0, mr: 1, mt: "1px" }}
                              />
                              <Box>
                                <Typography
                                  fontWeight="medium"
                                  sx={{
                                    color: isActive
                                      ? themes.stepActiveColor
                                      : "inherit",
                                  }}
                                >
                                  {opt.label}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mt: 0.5 }}
                                >
                                  {opt.description}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        );
                      })}
                    </RadioGroup>
                  </Box>
                )}

                {watch("break_deduction_applicability_type") !== "Hours" &&
                  getActiveBreakDeductionRule() &&
                  (() => {
                    const activeRule = getActiveBreakDeductionRule();
                    const { typeField, durationField } =
                      breakDeductionRuleFieldMap[activeRule];
                    const activeType = watch(typeField);
                    const activeDuration = watch(durationField);
                    return (
                      <Box
                        sx={{
                          border: `1px solid ${themes.miliedLightGray}`,
                          borderRadius: 2,
                          overflow: "hidden",
                          p: 2,
                        }}
                      >
                        <Typography fontWeight="medium" sx={{ mb: 0.5 }}>
                          Deduction Duration
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1.5 }}
                        >
                          Choose how much time to deduct for the selected
                          rule.
                        </Typography>
                        <RadioGroup
                          row
                          value={activeType}
                          onChange={(event) => {
                            setValue(typeField, event.target.value);
                            setBreakErrors((prev) => ({
                              ...prev,
                              [durationField]: "",
                            }));
                          }}
                          sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}
                        >
                          {getBreakDeductionTypeOptions(activeRule).map(
                            (opt) => {
                              const isActive = activeType === opt.id;
                              return (
                                <Box
                                  key={opt.id}
                                  sx={{
                                    ...cardStyle(isActive),
                                    flex: "1 1 260px",
                                    m: 0,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "flex-start",
                                    }}
                                  >
                                    <Radio
                                      value={opt.id}
                                      sx={{ p: 0, mr: 1, mt: "1px" }}
                                    />
                                    <Box sx={{ flex: 1 }}>
                                      <Typography
                                        fontWeight="medium"
                                        sx={{
                                          color: isActive
                                            ? themes.stepActiveColor
                                            : "inherit",
                                        }}
                                      >
                                        {opt.label}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                          mt: 0.5,
                                          mb:
                                            isActive && opt.id === "custom"
                                              ? 1
                                              : 0,
                                        }}
                                      >
                                        {opt.description}
                                      </Typography>
                                      {isActive && opt.id === "custom" && (
                                        <Box
                                          display="flex"
                                          alignItems="center"
                                          gap={1}
                                        >
                                          <TextField
                                            size="small"
                                            value={activeDuration}
                                            onChange={(event) =>
                                              handleBreakNumberChange(
                                                durationField,
                                                event.target.value,
                                              )
                                            }
                                            sx={{ width: 90 }}
                                            error={Boolean(
                                              breakErrors[durationField],
                                            )}
                                            helperText={
                                              breakErrors[durationField]
                                            }
                                          />
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                          >
                                            minutes
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>
                                  </Box>
                                </Box>
                              );
                            },
                          )}
                        </RadioGroup>

                        <Box sx={{ ...infoMessageStyle, ml: 0, mr: 0, mt: 2 }}>
                          <BreakDeductionRuleExample
                            rule={activeRule}
                            amountType={activeType}
                            duration={activeDuration}
                          />
                        </Box>
                      </Box>
                    );
                  })()}

                {watch("break_deduction_applicability_type") !== "Hours" && (
                  <Box
                    sx={{
                      border: `1px solid ${themes.miliedLightGray}`,
                      borderRadius: 2,
                      p: 1.5,
                      backgroundColor: "#F0F6FF",
                    }}
                  >
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Note:</strong>
                    </Typography>
                    <Box
                      component="ul"
                      sx={{ m: 0, pl: 2.5, color: "text.secondary" }}
                    >
                      <Typography component="li" variant="body2">
                        Actual recorded break time is always deducted.
                      </Typography>
                      <Typography component="li" variant="body2">
                        Only one break deduction rule can be selected at a
                        time.
                      </Typography>
                      <Typography component="li" variant="body2">
                        The rule determines if and when an additional
                        deduction is applied, and how much to deduct.
                      </Typography>
                      <Typography component="li" variant="body2">
                        This setting affects attendance calculation and
                        payroll time computation.
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          <Box
            sx={{
              ml: 1,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <CustomButton actionName="Next" typeName="submit" />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
